import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * 풀스크린 이미지 라이트박스
 * - currentIndex 가 null 이면 닫힘 (mount 안 함)
 * - 키보드: Esc 닫기 / ← → 이전·다음
 * - 백드롭 클릭 → 닫기
 * - 모바일 스와이프 → 이전·다음
 * - 1장이면 화살표 / 카운터 숨김
 */
export default function ImageLightbox({
  images,
  currentIndex,
  onClose,
  onIndexChange,
  alt = "",
}) {
  const touchStartRef = useRef(null);
  const isOpen = currentIndex != null;
  const hasMultiple = images && images.length > 1;

  const goPrev = () => {
    if (!hasMultiple) return;
    const next = currentIndex - 1 < 0 ? images.length - 1 : currentIndex - 1;
    onIndexChange(next);
  };
  const goNext = () => {
    if (!hasMultiple) return;
    const next = (currentIndex + 1) % images.length;
    onIndexChange(next);
  };

  // 키보드 단축키
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentIndex]);

  // body 스크롤 잠금
  useEffect(() => {
    if (!isOpen) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = orig;
    };
  }, [isOpen]);

  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartRef.current == null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current;
    touchStartRef.current = null;
    if (deltaX > 60) goPrev();
    else if (deltaX < -60) goNext();
  };

  if (!isOpen) return null;

  // Portal 로 document.body 에 직접 mount
  // (시트의 transform 컨테이너 영향 없이 진짜 viewport 풀스크린)
  return createPortal(
    <div
      className="cf-lightbox"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <button
        type="button"
        className="cf-lightbox__close"
        onClick={onClose}
        aria-label="닫기"
      >
        ✕
      </button>

      {hasMultiple && (
        <button
          type="button"
          className="cf-lightbox__nav cf-lightbox__nav--prev"
          onClick={goPrev}
          aria-label="이전"
        >
          ‹
        </button>
      )}

      <img
        className="cf-lightbox__image"
        src={images[currentIndex]}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
      />

      {hasMultiple && (
        <button
          type="button"
          className="cf-lightbox__nav cf-lightbox__nav--next"
          onClick={goNext}
          aria-label="다음"
        >
          ›
        </button>
      )}

      {hasMultiple && (
        <div className="cf-lightbox__counter">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>,
    document.body
  );
}
